/**
 * SpiffApprovalsPanel — the ASM board's SPIFFs tab (Oct 27, 2026).
 *
 * Greg: "lets adjust the spiff approval to alex poma on monday morning". The weekly run (Monday 12:05 PM ET)
 * records last week's SPIFFs waiting here; Alex ticks what is right and presses Approve, which makes one Books
 * bill per person (spiffs_approve); Shannon pays them in Books on Tuesday. Chad or Greg approve when Alex hasn't.
 * A line that should not be paid is removed with a reason (spiff_remove). Spiffs a manager gives by hand during
 * the week wait here too.
 *
 * Reads through spiffs_for_approval (an approver sees every waiting line, including a 5-Star Review line of a rep
 * outside Alex's team that the table's own policy would hide). Realtime-refreshes on spiffs.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { fmtUSD } from "../../lib/analytics";
import { canApproveSpiffs, groupWaiting, spiffWeekOf, weekLabel, type ApprovalSpiff } from "../../lib/spiffApproval";
import { FIVE_STAR_REVIEW_SPIFF } from "../../lib/weeklySpiffs";
import { approveSpiffs, fetchSpiffsForApproval, removeSpiff } from "./crewQueries";

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" });
}

export default function SpiffApprovalsPanel({ onChanged }: { onChanged?: () => void }) {
  const { role } = useAuth();
  const allowed = canApproveSpiffs(role);
  const [rows, setRows] = useState<ApprovalSpiff[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // Unticked lines stay waiting; everything is ticked by default.
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; reason: string } | null>(null);

  const load = useCallback(async () => {
    if (!allowed) { setRows([]); return; }
    try {
      setRows(await fetchSpiffsForApproval(14));
    } catch (e) {
      setErr(`Couldn't load the SPIFFs: ${formatSupabaseError(e)}`);
      setRows([]);
    }
  }, [allowed]);

  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("spiffs", load);

  const weeks = useMemo(() => groupWaiting(rows ?? []), [rows]);
  const waiting = useMemo(() => weeks.flatMap((w) => w.people.flatMap((p) => p.rows)), [weeks]);
  const ticked = waiting.filter((r) => !unticked.has(r.id));
  const tickedCents = ticked.reduce((s, r) => s + r.amount_cents, 0);
  const approvedRecently = useMemo(
    () => (rows ?? []).filter((r) => !!r.approved_at).sort((a, b) => (b.approved_at ?? "").localeCompare(a.approved_at ?? "")),
    [rows],
  );

  function toggle(id: string, on: boolean) {
    setUnticked((prev) => { const n = new Set(prev); if (on) n.delete(id); else n.add(id); return n; });
  }

  async function approve() {
    if (ticked.length === 0) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      const out = await approveSpiffs(ticked.map((r) => r.id));
      setNote(`Approved ${out.approved} SPIFF${out.approved === 1 ? "" : "s"} (${fmtUSD(out.totalCents / 100)}) and sent them to Books. Shannon pays them there.`);
      setUnticked(new Set());
      await load();
      onChanged?.();
    } catch (e) {
      setErr(`Couldn't approve: ${formatSupabaseError(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!removing) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      await removeSpiff(removing.id, removing.reason);
      setRemoving(null);
      await load();
      onChanged?.();
    } catch (e) {
      setErr(`Couldn't remove it: ${formatSupabaseError(e)}`);
    } finally {
      setBusy(false);
    }
  }

  if (!allowed) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600">
        Alex (the ASM) approves the weekly SPIFFs here; Chad or Greg when he hasn't.
      </div>
    );
  }
  if (rows === null) return <div className="text-sm text-slate-500">Loading SPIFFs…</div>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Last week's SPIFFs come in Monday at noon. Check each line, untick or remove what is wrong, then approve:
        each person gets one bill in Books and Shannon pays them on Tuesday. A rep's ASM 50% line is separate:
        removing a rep's line does not change it.
      </p>

      {err && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">⚠ {err}</div>}
      {note && <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">✓ {note}</div>}

      {weeks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
          <div className="text-sm font-medium text-slate-700">No SPIFFs waiting for approval.</div>
          <div className="mt-1 text-xs text-slate-500">Last week's SPIFFs show up here Monday at noon.</div>
        </div>
      ) : (
        <>
          {weeks.map((w) => (
            <div key={w.monday} className="overflow-hidden rounded-xl border border-orange-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-orange-50/60 px-4 py-2.5">
                <div className="text-sm font-semibold text-slate-900">{weekLabel(w.monday)}</div>
                <div className="text-xs text-slate-600">{w.count} line{w.count === 1 ? "" : "s"} · {fmtUSD(w.cents / 100)}</div>
              </div>
              {w.people.map((p) => (
                <div key={p.user_id} className="border-b border-slate-100 last:border-b-0">
                  <div className="flex items-center justify-between gap-2 px-4 pt-2.5 text-sm">
                    <span className="font-semibold text-slate-800">{p.name}</span>
                    <span className="font-semibold text-slate-800">{fmtUSD(p.cents / 100)}</span>
                  </div>
                  <div className="divide-y divide-slate-50 pb-1.5">
                    {p.rows.map((r) => {
                      const isOverride = r.source === "asm_override";
                      const isReview = r.name === FIVE_STAR_REVIEW_SPIFF.name;
                      const isRemoving = removing?.id === r.id;
                      return (
                        <div key={r.id} className="px-4 py-1.5">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={!unticked.has(r.id)}
                              disabled={busy}
                              onChange={(e) => toggle(r.id, e.target.checked)}
                              className="h-4 w-4 shrink-0 accent-emerald-600"
                              aria-label={`Approve ${r.name}`}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[13px] text-slate-800">
                                {r.name}
                                {isOverride && <span className="ml-1.5 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700">ASM 50%</span>}
                                {isReview && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">⭐ 5★ review</span>}
                                {r.source === "manual" && <span className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">given by hand</span>}
                              </div>
                              {r.job_address && <div className="truncate text-[11px] text-slate-500">{r.job_address}</div>}
                            </div>
                            <div className="whitespace-nowrap text-[13px] font-semibold text-slate-800">{fmtUSD(r.amount_cents / 100)}</div>
                            <button
                              type="button"
                              onClick={() => setRemoving(isRemoving ? null : { id: r.id, reason: "" })}
                              disabled={busy}
                              className="whitespace-nowrap rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                            >
                              {isRemoving ? "Keep" : "Remove…"}
                            </button>
                          </div>
                          {isRemoving && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-7">
                              <input
                                type="text"
                                value={removing.reason}
                                onChange={(e) => setRemoving({ id: r.id, reason: e.target.value })}
                                placeholder="Why it should not be paid"
                                className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-base sm:text-sm"
                              />
                              <button
                                type="button"
                                onClick={remove}
                                disabled={busy || removing.reason.trim().length < 5}
                                className="rounded bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                              >
                                Remove line
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-sm text-slate-700">
              <span className="font-semibold">{ticked.length}</span> of {waiting.length} ticked · <span className="font-semibold">{fmtUSD(tickedCents / 100)}</span>
            </div>
            <button
              type="button"
              onClick={approve}
              disabled={busy || ticked.length === 0}
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? "Working…" : `Approve ${ticked.length} & send to Books`}
            </button>
          </div>
        </>
      )}

      {approvedRecently.length > 0 && (
        <details className="rounded-xl border border-slate-200 bg-white">
          <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700">
            Approved in the last 2 weeks ({approvedRecently.length})
          </summary>
          <div className="divide-y divide-slate-100 border-t border-slate-100">
            {approvedRecently.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-1.5 text-[13px]">
                <div className="min-w-0 flex-1 truncate text-slate-700">
                  {r.recipient_name} · {r.name}
                  <span className="text-slate-400"> · {weekLabel(spiffWeekOf(r.claimed_at)).replace("Week of ", "")}</span>
                </div>
                <div className="whitespace-nowrap font-semibold text-slate-800">{fmtUSD(r.amount_cents / 100)}</div>
                <div className="hidden whitespace-nowrap text-[11px] text-slate-500 sm:block">
                  {r.paid_at ? `paid ${fmtDay(r.paid_at)}` : r.books_bill_id ? "in Books" : "approved"}
                  {r.approved_by_name ? ` · ${r.approved_by_name.split(" ")[0]} ${fmtDay(r.approved_at as string)}` : ""}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
